// /home/szymon/kreator2/frontend/src/components/editor/useEditorActions.js - Attempt 4 (Explicit offsetX via write_to_file)
import { useCallback, useEffect, useRef } from "react";
import useUndoRedo from "../../hooks/useUndoRedo";

export function useEditorActions({
  pages,
  setPages,
  backendUrl,
  publicMode,
  isAccepted,
  doAutoSave,
  changesCountRef,
  currentPage
}) {
  const [
    currentVal,
    setNewState,
    undo,
    redo,
    canUndo,
    canRedo,
    resetHistory
  ] = useUndoRedo(pages);

  // Flaga do wykrycia "undo/redo w toku", by nie generować kolejnego wpisu w historii
  const undoRedoInProgressRef = useRef(false);

  // Zapamiętanie poprzedniego pages
  const prevPagesRef = useRef(pages);

  useEffect(() => {
    const changed = pages !== prevPagesRef.current;
    prevPagesRef.current = pages;

    if (!changed) return;

    // Jeżeli to "undo/redo" – skip nowego setNewState
    if (undoRedoInProgressRef.current) {
      console.log("[useEditorActions] pages changed => from undo/redo => skip setNewState");
      undoRedoInProgressRef.current = false;
      return;
    }

    // Inaczej – normalna zmiana => wstawiamy do historii
    console.log("[useEditorActions] pages changed => setNewState (no slicing).");
    setNewState(pages);
  }, [pages, setNewState]);

  const isLocked = useCallback(() => publicMode && isAccepted, [publicMode, isAccepted]);

  const updateCurrentPage = useCallback(
    (fn) => {
      setPages((prev) => {
        if (!prev || !prev.length) return prev;
        if (currentPage < 0 || currentPage >= prev.length) return prev;
        const arr = [...prev];
        const pageCopy = { ...arr[currentPage] };
        arr[currentPage] = fn(pageCopy) || pageCopy;
        return arr;
      });
    },
    [setPages, currentPage]
  );

  const handleAddNewText = useCallback(() => {
    if (isLocked()) return;
    updateCurrentPage((pg) => {
      let maxSubZ = 0;
      [...pg.texts, ...pg.images, ...pg.svgs].forEach((o) => {
        if ((o.subZ || 0) > maxSubZ) {
          maxSubZ = o.subZ || 0;
        }
      });
      const newId = "text-" + Date.now();
      return {
        ...pg,
        texts: [
          ...pg.texts,
          {
            id: newId,
            text: "Nowy tekst",
            x: 50,
            y: 50,
            color: [0, 0, 0],
            alignment: "left",
            fontName: "sans-serif",
            fontSize: 24,
            rotation: 0,
            scaleX: 1,
            scaleY: 1,
            zGroup: 3,
            subZ: maxSubZ + 1,
            flags: {
              noPrint: false,
              gold: false,
              locked: false,
              personalize: false
            }
          }
        ]
      };
    });
    changesCountRef.current++;
    if (changesCountRef.current >= 5) {
      doAutoSave();
      changesCountRef.current = 0;
    }
  }, [updateCurrentPage, isLocked, changesCountRef, doAutoSave]);

  const handleImageDropped = useCallback(
    async ({ file, name }) => {
      if (isLocked()) return;
      if (!file) return;
      try {
        const formData = new FormData();
        formData.append("file", file);
        const url = publicMode
          ? `${backendUrl}/client/uploads`
          : `${backendUrl}/admin/uploads`;
        const res = await fetch(url, {
          method: "POST",
          credentials: "include",
          body: formData
        });
        if (!res.ok) {
          throw new Error("Błąd uploadu => status=" + res.status);
        }
        const data = await res.json();
        if (!data.success || !data.filePath) {
          throw new Error(data.message || "Brak filePath");
        }
        const filePath = data.filePath;

        const prefixRaw = publicMode
          ? `${backendUrl}/client/raw-file?path=`
          : `${backendUrl}/admin/raw-file?path=`;
        const finalUrl = prefixRaw + encodeURIComponent(filePath);

        const img = new window.Image();
        img.crossOrigin = "anonymous";
        await new Promise((resolve, reject) => {
          img.onload = () => resolve(true);
          img.onerror = (err) => reject(err);
          img.src = finalUrl;
        });
        let origW = img.naturalWidth || 100;
        let origH = img.naturalHeight || 100;

        updateCurrentPage((pg) => {
          let scale = 1.0;
          if (origW > pg.baseWidth || origH > pg.baseHeight) {
            scale = Math.min(pg.baseWidth / origW, pg.baseHeight / origH);
          }
          const finalW = Math.round(origW * scale);
          const finalH = Math.round(origH * scale);

          let maxSubZ = 0;
          [...pg.texts, ...pg.images, ...pg.svgs].forEach((o) => {
            if ((o.subZ || 0) > maxSubZ) {
              maxSubZ = o.subZ || 0;
            }
          });
          const lowered = (name || "").toLowerCase();
          const isSvg = lowered.endsWith(".svg");
          const newId = (isSvg ? "svg-" : "img-") + Date.now();
          const commonProps = {
            id: newId,
            x: 50,
            y: 50,
            width: finalW,
            height: finalH,
            rotation: 0,
            scaleX: 1,
            scaleY: 1,
            flags: {
              noPrint: false,
              gold: false,
              locked: false,
              personalize: false
            },
            subZ: maxSubZ + 1
          };
          if (isSvg) {
            return {
              ...pg,
              svgs: [
                ...pg.svgs,
                {
                  ...commonProps,
                  src: finalUrl,
                  zGroup: 2
                }
              ]
            };
          }
          return {
            ...pg,
            images: [
              ...pg.images,
              {
                ...commonProps,
                src: finalUrl,
                zGroup: 1
              }
            ]
          };
        });
        changesCountRef.current++;
        if (changesCountRef.current >= 5) {
          doAutoSave();
          changesCountRef.current = 0;
        }
      } catch (err) {
        console.error("[handleImageDropped]", err);
        window.alert("Błąd uploadu: " + err.message);
      }
    },
    [isLocked, publicMode, backendUrl, updateCurrentPage, doAutoSave, changesCountRef]
  );

  const handleChangeText = useCallback(
    (chg) => {
      if (isLocked()) return;
      if (!chg?.id) return;
      updateCurrentPage((pg) => {
        const nextTexts = pg.texts.map((t) =>
          t.id === chg.id ? { ...t, ...chg } : t
        );
        return { ...pg, texts: nextTexts };
      });
      changesCountRef.current++;
      if (changesCountRef.current >= 5) {
        doAutoSave();
        changesCountRef.current = 0;
      }
    },
    [isLocked, updateCurrentPage, doAutoSave, changesCountRef]
  );

  const handleChangeImage = useCallback(
    (chg) => {
      if (isLocked()) return;
      if (!chg?.id) return;
      updateCurrentPage((pg) => {
        const nextImages = pg.images.map((im) =>
          im.id === chg.id ? { ...im, ...chg } : im
        );
        return { ...pg, images: nextImages };
      });
      changesCountRef.current++;
      if (changesCountRef.current >= 5) {
        doAutoSave();
        changesCountRef.current = 0;
      }
    },
    [isLocked, updateCurrentPage, doAutoSave, changesCountRef]
  );

  // *** POPRAWKA handleChangeSvg ***
  // Dodajemy logikę: jeżeli brak w tablicy => dodaj nowy, w przeciwnym razie aktualizuj istniejący
  const handleChangeSvg = useCallback(
    (chg) => {
      if (isLocked()) return;
      if (!chg?.id) return;
      updateCurrentPage((pg) => {
        const existingIndex = pg.svgs.findIndex((sv) => sv.id === chg.id);
        if (existingIndex >= 0) {
          // aktualizacja istniejącego
          const nextSvgs = pg.svgs.map((sv, idx) =>
            idx === existingIndex ? { ...sv, ...chg } : sv
          );
          return { ...pg, svgs: nextSvgs };
        } else {
          // dodawanie nowego
          return { ...pg, svgs: [...pg.svgs, { ...chg }] };
        }
      });
      changesCountRef.current++;
      if (changesCountRef.current >= 5) {
        doAutoSave();
        changesCountRef.current = 0;
      }
    },
    [isLocked, updateCurrentPage, doAutoSave, changesCountRef]
  );

  const handleColorChange = useCallback(
    (obj, newColorArr) => {
      if (isLocked()) return;
      if (!obj?.id || !newColorArr) return;
      if (obj._type === "text") {
        handleChangeText({ id: obj.id, color: newColorArr });
      } else if (obj._type === "svg") {
        handleChangeSvg({ id: obj.id, color: newColorArr });
      }
    },
    [isLocked, handleChangeText, handleChangeSvg]
  );

  const handleLayerUp = useCallback(
    (shapeId) => {
      if (isLocked()) return;
      if (!shapeId) return;
      updateCurrentPage((pg) => {
        const nTexts = pg.texts.map((t) =>
          t.id === shapeId ? { ...t, subZ: (t.subZ || 0) + 1 } : t
        );
        const nImages = pg.images.map((im) =>
          im.id === shapeId ? { ...im, subZ: (im.subZ || 0) + 1 } : im
        );
        const nSvgs = pg.svgs.map((sv) =>
          sv.id === shapeId ? { ...sv, subZ: (sv.subZ || 0) + 1 } : sv
        );
        return { ...pg, texts: nTexts, images: nImages, svgs: nSvgs };
      });
      changesCountRef.current++;
      if (changesCountRef.current >= 5) {
        doAutoSave();
        changesCountRef.current = 0;
      }
    },
    [isLocked, updateCurrentPage, changesCountRef, doAutoSave]
  );

  const handleLayerDown = useCallback(
    (shapeId) => {
      if (isLocked()) return;
      if (!shapeId) return;
      updateCurrentPage((pg) => {
        const nTexts = pg.texts.map((t) =>
          t.id === shapeId ? { ...t, subZ: (t.subZ || 0) - 1 } : t
        );
        const nImages = pg.images.map((im) =>
          im.id === shapeId ? { ...im, subZ: (im.subZ || 0) - 1 } : im
        );
        const nSvgs = pg.svgs.map((sv) =>
          sv.id === shapeId ? { ...sv, subZ: (sv.subZ || 0) - 1 } : sv
        );
        return { ...pg, texts: nTexts, images: nImages, svgs: nSvgs };
      });
      changesCountRef.current++;
      if (changesCountRef.current >= 5) {
        doAutoSave();
        changesCountRef.current = 0;
      }
    },
    [isLocked, updateCurrentPage, changesCountRef, doAutoSave]
  );

  // --- ZMIANA: Aplikowanie zmian z autosnap (teraz uwzględnia offsetX) ---
  const applyAutosnapChanges = useCallback((changes) => {
    if (isLocked()) return;
    if (!changes || changes.length === 0) {
      console.log("[applyAutosnapChanges] Brak zmian do zastosowania.");
      return;
    }

    console.log(`[applyAutosnapChanges] Stosowanie ${changes.length} zmian...`, changes);

    setPages((prevPages) => {
      // Tworzymy głęboką kopię, aby zapewnić niemutowalność
      const nextPages = JSON.parse(JSON.stringify(prevPages));
      let appliedCount = 0;

      changes.forEach(change => {
        // Odczytujemy newX (obliczona lewa krawędź), textWidth (rzeczywista szerokość) i alignment
        const { pageIndex, id, newX, textWidth, alignment } = change;
        if (pageIndex >= 0 && pageIndex < nextPages.length) {
          const page = nextPages[pageIndex];
          if (page.texts) {
            const textIndex = page.texts.findIndex(t => t.id === id);
            if (textIndex !== -1) {
              // Ustawiamy obliczoną lewą krawędź jako x
              nextPages[pageIndex].texts[textIndex].x = newX;

              // Obliczamy i ustawiamy offsetX JAWNIE na podstawie rzeczywistej szerokości
              let calculatedOffsetX = 0;
              if (alignment === 'center' && typeof textWidth === 'number' && textWidth > 0) {
                calculatedOffsetX = textWidth / 2;
              } else if (alignment === 'right' && typeof textWidth === 'number' && textWidth > 0) {
                // Dodano obsługę wyrównania do prawej, jeśli będzie potrzebna
                calculatedOffsetX = textWidth;
              }
              nextPages[pageIndex].texts[textIndex].offsetX = calculatedOffsetX;
              // Nie modyfikujemy width

              // Dodano sprawdzenie typów przed toFixed
              console.log(`[applyAutosnapChanges] Applying to ID ${id}: newX=${typeof newX === 'number' ? newX.toFixed(2) : newX}, calculatedOffsetX=${typeof calculatedOffsetX === 'number' ? calculatedOffsetX.toFixed(2) : calculatedOffsetX} (Align: ${alignment}, Width: ${typeof textWidth === 'number' ? textWidth.toFixed(2) : textWidth})`);
              appliedCount++;
            } else {
              console.warn(`[applyAutosnapChanges] Nie znaleziono tekstu o ID ${id} na stronie ${pageIndex}`);
            }
          }
        } else {
          console.warn(`[applyAutosnapChanges] Nieprawidłowy pageIndex: ${pageIndex}`);
        }
      });

      console.log(`[applyAutosnapChanges] Zastosowano ${appliedCount} zmian.`);
      // Zwracamy zmodyfikowaną kopię stanu
      return nextPages;
    });

    // Zliczanie zmian i autosave (można by to zrobić bardziej granularnie, ale na razie liczymy jako jedną dużą zmianę)
    changesCountRef.current++;
    if (changesCountRef.current >= 5) {
      doAutoSave();
      changesCountRef.current = 0;
    }
    // triggerForceRefresh nie jest tu potrzebny, bo setPages wywoła re-render
  }, [isLocked, setPages, changesCountRef, doAutoSave]);
  // --- KONIEC ZMIANY ---

  const handleMoveToTop = useCallback(() => {
    if (isLocked()) return;
    // nie usuwamy funkcji
  }, [isLocked]);

  const handleMoveToBottom = useCallback(() => {
    if (isLocked()) return;
    // nie usuwamy funkcji
  }, [isLocked]);

  const handleToggleFlag = useCallback(
    (flagName) => {
      if (isLocked()) return;
      if (!flagName) return;
      // nie usuwamy istniejącej funkcji
    },
    [isLocked]
  );

  const doUndo = useCallback(() => {
    console.log("[useEditorActions] => doUndo");
    undoRedoInProgressRef.current = true;
    const undone = undo();
    if (undone) {
      setPages(undone);
    } else {
      undoRedoInProgressRef.current = false;
    }
  }, [undo, setPages]);

  const doRedo = useCallback(() => {
    console.log("[useEditorActions] => doRedo");
    undoRedoInProgressRef.current = true;
    const redone = redo();
    if (redone) {
      setPages(redone);
    } else {
      undoRedoInProgressRef.current = false;
    }
  }, [redo, setPages]);

  return {
    handleAddNewText,
    handleImageDropped,
    handleChangeText,
    handleChangeImage,
    handleChangeSvg,
    handleColorChange,
    handleLayerUp,
    handleLayerDown,
    handleMoveToTop,
    handleMoveToBottom,
    handleToggleFlag,
    applyAutosnapChanges, // Zwracamy zmodyfikowaną funkcję
    undo: doUndo,
    redo: doRedo,
    canUndo,
    canRedo
  };
}
